'use client';

import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import FlyoverEntry, { type FlyoverEntryClickSource } from '@alltrails/flyover/components/FlyoverEntry';
import { BREAKPOINT_MD_MARGIN, BREAKPOINT_MD_SIZE, BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import logFlyoverStartButtonTapped from '@alltrails/amplitude/events/logFlyoverStartButtonTapped';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import { setFlyoverOpen } from '@alltrails/flyover/redux/flyover';
import { atMapsToGeojson } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import getCoordinatesFromFeatures from '@alltrails/maps/utils/getCoordinatesFromFeatures';
import { LngLat } from '@alltrails/maps/types/Geo';
import turfLength from '@turf/length';
import { lineString } from '@turf/helpers';
import cardStyles from '../../components/CardModule/CardModule.module.scss';
import { useDispatch, useSelector } from '@/utils/redux';
import getMap from '@/utils/getMap';
import getElevationProfile from '@/utils/getElevationProfile';

const FlyoverMapModal = dynamic(() => import('@alltrails/flyover/components/FlyoverMapModal'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
};
const TrailFlyoverEntry = ({ trailDetails }: Props) => {
  const publicBaseUrl = usePublicBaseUrl();
  const isMobileWidth = useIsMobileSizedScreen();

  const { id, trailGeoStats, name, verifiedMapId } = trailDetails;
  const staticMapUrl = getStaticMapUrl({
    baseUrl: publicBaseUrl,
    id: trailDetails.id,
    scale: 2,
    show3d: true,
    size: isMobileWidth ? '342x424' : '1280x582',
    type: 'trails'
  });

  const dispatch = useDispatch();
  const isFlyoverOpen = useSelector(state => state.flyover.isOpen);

  const [isLoading, setIsLoading] = useState(false);
  const [didError, setDidError] = useState(false);
  const [routeCoordinates, setRouteCoordinates] = useState<LngLat[]>();
  const [segmentedElevations, setSegmentedElevations] = useState<number[]>();

  const populateFlyoverData = useCallback(async () => {
    const [map, elevationProfile] = await Promise.all([getMap(verifiedMapId), getElevationProfile(verifiedMapId)]);
    const coordinates = getCoordinatesFromFeatures(atMapsToGeojson([map]));
    if (coordinates?.length) {
      setRouteCoordinates(coordinates);
    }

    if (elevationProfile.smoothedElevations?.length) {
      setSegmentedElevations(elevationProfile.smoothedElevations.map(({ elevation }) => elevation));
    }
  }, [verifiedMapId]);

  const onFlyoverEntryClick = useCallback(
    (source: FlyoverEntryClickSource) => {
      setIsLoading(true);

      logFlyoverStartButtonTapped({
        source: 'Trail Details',
        trail_id: id,
        cta_text: source === 'playButton' ? 'play' : 'preview route'
      });

      populateFlyoverData()
        .then(() => {
          dispatch(setFlyoverOpen(true));
        })
        .catch(() => {
          setDidError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, id, populateFlyoverData]
  );

  const hasFlyover = trailGeoStats.length !== undefined && trailGeoStats.length > 0;

  const routeInfo = useMemo<ComponentProps<typeof FlyoverMapModal>['routeInfo'] | undefined>(() => {
    if (!routeCoordinates || !segmentedElevations) {
      return undefined;
    }

    // Use 2D distance from coordinates for animation (keeps blue dot in sync with turfAlong positioning)
    // Use server-provided distance for display (may include 3D calculations)
    const totalDistance = turfLength(lineString(routeCoordinates), { units: 'meters' });
    const displayDistance = trailGeoStats.length ?? totalDistance;

    return [
      {
        id,
        name,
        routeCoordinates,
        segmentedElevations,
        totalDistance, // Used for animation timing and blue dot positioning
        displayDistance, // Used for stats overlay display
        totalElevationGain: trailGeoStats.elevationGain ?? 0
      }
    ];
  }, [id, name, routeCoordinates, segmentedElevations, trailGeoStats.elevationGain, trailGeoStats.length]);

  return (
    <>
      <FlyoverEntry
        className={cardStyles.card}
        flyoverUnavailable={!hasFlyover || didError}
        isLoading={isLoading}
        innerClassName={cardStyles.inner}
        onClick={onFlyoverEntryClick}
        routeName={name}
        staticMapSizes={`(max-width: ${BREAKPOINT_MD_SIZE + BREAKPOINT_MD_MARGIN}px 1165px), (max-width: ${BREAKPOINT_SM_SIZE}px) 688px`}
        staticMapUrl={staticMapUrl}
        titleClassName={classNames(cardStyles.cardTitle, cardStyles.lightCardTitle)}
      />
      {routeInfo && <FlyoverMapModal analyticsInfo={{ source: 'Trail Details', trailId: id }} isOpen={isFlyoverOpen} routeInfo={routeInfo} />}
    </>
  );
};

export default TrailFlyoverEntry;
